import React, { useState } from "react";
import type { ChangesV2WorkflowResult } from "@aidkitorg/aidkit/lib/distro/review/workflows/changesV2Workflow";
import type { Diff } from "@aidkitorg/types/lib/utils/jsonDiff";
import WorkflowReview from "./WorkflowReview";
import { Heading, Alert, Tabs, Badge } from "@aidkitorg/component-library";

type DiffTypeCounts = { added: number; removed: number; changed: number };

function countDiffs(diffs: Diff[]): DiffTypeCounts {
  const counts: DiffTypeCounts = { added: 0, removed: 0, changed: 0 };
  for (const d of diffs) {
    if (d.type === 'added') counts.added++;
    else if (d.type === 'removed') counts.removed++;
    else if (d.type === 'changed') counts.changed++;
  }
  return counts;
}

function DiffCountsSummary({ counts }: { counts: DiffTypeCounts }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium">
      {counts.added > 0 && <span className="text-green-600">+{counts.added}</span>}
      {counts.removed > 0 && <span className="text-red-600">-{counts.removed}</span>}
      {counts.changed > 0 && <span className="text-yellow-600">~{counts.changed}</span>}
    </span>
  );
}

function DiffTypeBadge({ type }: { type: Diff['type'] }) {
  if (type === 'added') {
    return <span className="inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">added</span>;
  }
  if (type === 'removed') {
    return <span className="inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">removed</span>;
  }
  return <span className="inline-block px-1.5 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">changed</span>;
}

function ValueDisplay({ value, variant }: { value: unknown; variant: 'old' | 'new' }) {
  const bg = variant === 'old' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800';
  const prefix = variant === 'old' ? '-' : '+';
  const text = value === undefined ? '' : JSON.stringify(value, null, 2);
  if (!text) return null;
  return (
    <pre className={`text-xs font-mono border rounded px-2 py-1 whitespace-pre-wrap break-all ${bg}`}>
      {prefix} {text}
    </pre>
  );
}

function DiffRow({ diff }: { diff: Diff }) {
  return (
    <div className="border border-gray-200 rounded-lg p-3 space-y-1.5 bg-white">
      <div className="flex flex-wrap items-start gap-2">
        <DiffTypeBadge type={diff.type} />
        <code className="text-xs text-gray-700 bg-gray-100 rounded px-1 py-0.5 break-all">{diff.path}</code>
      </div>
      {diff.breadcrumbs && diff.breadcrumbs.length > 0 && (
        <div className="text-xs text-gray-500 flex flex-wrap items-center gap-1">
          {diff.breadcrumbs.map((crumb, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-gray-300">›</span>}
              <span>{crumb}</span>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="space-y-1 pt-0.5">
        {(diff.type === 'removed' || diff.type === 'changed') && (
          <ValueDisplay value={diff.oldValue} variant="old" />
        )}
        {(diff.type === 'added' || diff.type === 'changed') && (
          <ValueDisplay value={diff.newValue} variant="new" />
        )}
      </div>
    </div>
  );
}

type FilterType = 'all' | Diff['type'];

function SectionDiffs({ diffs }: { diffs: Diff[] }) {
  const [filter, setFilter] = useState<FilterType>('all');
  const counts = countDiffs(diffs);
  const filtered = filter === 'all' ? diffs : diffs.filter(d => d.type === filter);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'added', 'removed', 'changed'] as const).map(f => {
          const isActive = filter === f;
          let label: React.ReactNode;
          if (f === 'all') label = `All (${diffs.length})`;
          else if (f === 'added') label = `Added (${counts.added})`;
          else if (f === 'removed') label = `Removed (${counts.removed})`;
          else label = `Changed (${counts.changed})`;
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                isActive
                  ? 'bg-gray-800 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      {filtered.length === 0 ? (
        <p className="text-sm text-gray-500">No {filter} diffs in this section.</p>
      ) : (
        <div className="space-y-2">
          {filtered.map((diff, i) => (
            <DiffRow key={i} diff={diff} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ChangesV2WorkflowReview({
  distroCommitId,
  surveyHashMismatch,
  isPendingReview,
  isApproved,
  createdBy,
}: {
  distroCommitId: string;
  surveyHashMismatch: boolean;
  isPendingReview: boolean;
  isApproved: boolean;
  createdBy: string;
}) {
  const renderResults = (workflow: { result: ChangesV2WorkflowResult }) => {
    if (!workflow?.result?.success) return null;

    const { sections } = workflow.result;
    const totalCounts = sections.reduce<DiffTypeCounts>(
      (acc, { diffs }) => {
        const c = countDiffs(diffs);
        return { added: acc.added + c.added, removed: acc.removed + c.removed, changed: acc.changed + c.changed };
      },
      { added: 0, removed: 0, changed: 0 }
    );

    const tabs = sections.map(({ key, diffs }) => {
      const counts = countDiffs(diffs);
      return {
        name: (
          <span className="flex items-center gap-1.5">
            <span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
            <DiffCountsSummary counts={counts} />
          </span>
        ),
        key,
        content: <SectionDiffs diffs={diffs} />,
      };
    });

    return (
      <div className="space-y-6">
        {surveyHashMismatch && (isPendingReview || isApproved) && (
          <p className="text-red-500 font-bold">
            The survey has been saved outside of the review flow.
            This diff no longer reflects the current state of the survey.
          </p>
        )}
        {tabs.length > 0 ? (
          <>
            <p className="text-sm text-gray-500 ml-2">
              {createdBy} is suggesting{' '}
              <span className="text-green-600 font-medium">+{totalCounts.added} added</span>
              {', '}
              <span className="text-red-600 font-medium">-{totalCounts.removed} removed</span>
              {', '}
              <span className="text-yellow-600 font-medium">~{totalCounts.changed} changed</span>.
            </p>
            <Tabs tabs={tabs} justify="left" />
          </>
        ) : (
          <Alert title="No Diff Available" variant="warning">
            No diff available. The proposed survey is identical to the current survey.
          </Alert>
        )}
      </div>
    );
  };

  return (
    <WorkflowReview
      distroCommitId={distroCommitId}
      workflowName="changes_v2"
      title="Survey Changes"
      renderSummary={renderResults}
    />
  );
}
